import React, { Component } from "react";
import {
  Container,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  Table,
} from "reactstrap";
import PartnerAccessPanel from "../../components/PartnerAccessPanel";
import {
  API_BASE,
  PARTNER_API_PREFIX,
  INTEGRATIONS_PATH,
  PARTNER_PLANS_PATH,
  NAV_GROUPS,
  ENDPOINTS,
  VEHICLE_CATALOG_WORKFLOW,
  QUOTE_WORKFLOW,
  QUOTE_REQUEST_FIELDS,
  QUOTE_RESPONSE_FIELDS,
  VEHICLE_TYPES_RESPONSE_FIELDS,
  ACCEPT_REQUEST_FIELDS,
  ACCEPT_RESPONSE_FIELDS,
  ORDER_OBJECT_FIELDS,
  CANCEL_REQUEST_FIELDS,
  CONTACTS_REQUEST_FIELDS,
  FIELD_REFERENCE_SECTIONS,
  BOOKABLE_VEHICLE_SLUGS,
  LOAD_CATEGORIES,
  VEHICLE_TYPE_CODE_EXAMPLE,
  TRUCKTYPE_VALIDATION_NOTES,
  PARTNER_FLOW,
  PARTNER_FLOW_PLAIN,
  ORDER_STATUSES,
  ORDER_STATUS_FLOW,
  ERROR_CODES,
  MEASUREMENTS,
  API_CATALOG,
  BILLING_MODES,
  PAYMENT_PAYERS,
  CLAIMS_INFO,
  PARTNER_PLANS,
  ACCEPT_PACKS_DOCS,
} from "./partnerApiContent";
import {
  CodeBlock,
  Endpoint,
  partnerCurlExample,
  CompareTable,
  ErrorTable,
  FieldReferenceTable,
  VehicleSlugTable,
  CategorySlugTable,
  SidebarActions,
  AudienceCallout,
  CollapsibleTechnical,
  PlainFlow,
  StatusCards,
} from "./apiDocsShared";
import "./api-docs.css";

/** Partner surface always posts to the integrations host, not the monolith. */
const liveBase = API_BASE;

class PartnerApiDocs extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: "overview",
    };
  }

  componentDidMount() {
    try {
      const hash = (window.location.hash || "").replace(/^#/, "");
      if (hash && NAV_GROUPS.flatMap((g) => g.items).some((item) => item.id === hash)) {
        this.setState({ activeTab: hash });
      }
    } catch {
      /* noop */
    }
  }

  toggleTab = (tab) => {
    if (this.state.activeTab !== tab) this.setState({ activeTab: tab });
    try {
      const url = new URL(window.location.href);
      url.hash = tab;
      window.history.replaceState({}, "", url);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      /* noop */
    }
  };

  renderOverview() {
    return (
      <div className="qt-api-docs__section">
        <h2>Connect your systems to QikTruck</h2>
        <AudienceCallout>
          The Partner API lets your <strong>warehouse, ERP, TMS, or custom software</strong> get delivery
          prices, book drivers, and track trips automatically. Your IT team or integration partner handles
          the technical setup after QikTruck approves your account.
        </AudienceCallout>
        <p className="text-muted">
          Running an online store with a checkout? Use the{" "}
          <a href="/api-docs?product=embed">Website embed</a> instead. It does not require API keys in your theme.
        </p>

        <h3>How it works</h3>
        <PlainFlow steps={PARTNER_FLOW_PLAIN} />

        <h3>What you can do</h3>
        <ul className="qt-api-docs__benefits">
          <li><strong>Discover vehicles</strong> via GET /vehicle-types (trucktype + category slugs)</li>
          <li><strong>Get prices</strong> via POST /quotes before you commit to a booking</li>
          <li><strong>Book trips</strong> from orders in your own software</li>
          <li><strong>Track deliveries</strong> until the driver finishes</li>
          <li><strong>Manage keys &amp; usage</strong> in <a href={INTEGRATIONS_PATH}>Integrations</a></li>
        </ul>

        <CollapsibleTechnical title="Developer quick start">
          <p className="text-muted small">
            Authenticated routes live at <code>{liveBase.replace(/\/$/, "") + PARTNER_API_PREFIX}</code>.
            Use a <code>qt_live_</code> or <code>qt_test_</code> key issued after access review.
          </p>
          <div className="qt-api-flow">
            {PARTNER_FLOW.map((step, i) => (
              <React.Fragment key={step}>
                {i > 0 && <span className="qt-api-flow__arrow">→</span>}
                <span className="qt-api-flow__step">{step}</span>
              </React.Fragment>
            ))}
          </div>
          <CodeBlock label="Quick test - list vehicle types">
            {partnerCurlExample(liveBase, "GET", PARTNER_API_PREFIX + "/vehicle-types", null)}
          </CodeBlock>
        </CollapsibleTechnical>
      </div>
    );
  }

  renderRequestAccess() {
    return (
      <div className="qt-api-docs__section">
        <h2>Request access</h2>
        <AudienceCallout>
          Partner API access is reviewed by QikTruck. If you have not applied yet, use the form below. If you
          already applied, this page shows your current status instead of asking you to submit again.
        </AudienceCallout>
        <p className="text-muted">
          You can also manage access from <a href={INTEGRATIONS_PATH}>Integrations → Partner API</a>. Questions?{" "}
          <a href="mailto:support@qiktruck.co.za?subject=Partner%20API%20access">support@qiktruck.co.za</a>.
        </p>

        <PartnerAccessPanel sourceRef="customer-hq:api-docs:request-access" requireLogin />

        <CollapsibleTechnical title="API registration endpoint (for developers)">
          <Endpoint ep={ENDPOINTS.apiRegistration} />
        </CollapsibleTechnical>
      </div>
    );
  }

  renderAuth() {
    return (
      <div className="qt-api-docs__section">
        <h2>Authentication</h2>
        <AudienceCallout>
          Think of an API key like a password for your server. Your IT team adds it to requests from your
          backend only. <strong>Never</strong> put keys in a website, mobile app, or email.
        </AudienceCallout>
        <h3>Sandbox vs production</h3>
        <Table bordered size="sm" className="qt-api-table bg-white">
          <tbody>
            <tr>
              <td><strong>Sandbox key</strong></td>
              <td><code>qt_test_…</code> - practice integrations safely. No real production bookings.</td>
            </tr>
            <tr>
              <td><strong>Production key</strong></td>
              <td><code>qt_live_…</code> - live trips. Issued by QikTruck after you are ready to go live.</td>
            </tr>
            <tr>
              <td><strong>API address</strong></td>
              <td><code>{liveBase.replace(/\/$/, "") + PARTNER_API_PREFIX}</code></td>
            </tr>
          </tbody>
        </Table>
        <CollapsibleTechnical>
          <div className="qt-api-docs__callout">
            <code>Authorization: Bearer qt_live_…</code>
          </div>
        </CollapsibleTechnical>
      </div>
    );
  }

  renderVehicleTypes() {
    return (
      <div className="qt-api-docs__section">
        <h2>Vehicle catalog</h2>
        <AudienceCallout>
          <code>GET /v1/partner/vehicle-types</code> is its own endpoint. It returns the live list of bookable
          vehicles, valid <code>trucktype</code> slugs, and supported load <code>category</code> values. Call it
          before every quote - do not hard-code slugs in your integration.
        </AudienceCallout>

        <h3 className="h5 mt-4">How to use the catalog</h3>
        <PlainFlow
          steps={VEHICLE_CATALOG_WORKFLOW.map((s) => ({ title: s.title, detail: s.detail }))}
        />

        <h3 className="h5 mt-4">Example bookable trucktype values</h3>
        <p className="text-muted small">
          Illustrative slugs for open-body vehicles. Always call the catalog API in production - closed
          and fridge variants (e.g. <code>1.5tonclosed</code>) appear when <code>sell: true</code>.
        </p>
        <VehicleSlugTable slugs={BOOKABLE_VEHICLE_SLUGS} />

        <h3 className="h5 mt-4">Load category values</h3>
        <p className="text-muted small">
          Pass as <code>category</code> on POST /quotes. Must be supported by the chosen vehicle.
        </p>
        <CategorySlugTable categories={LOAD_CATEGORIES} />

        <h3 className="h5 mt-4">Sample code</h3>
        <CodeBlock label="Discover trucktype + category (JavaScript)">{VEHICLE_TYPE_CODE_EXAMPLE}</CodeBlock>

        <ul className="small text-muted">
          {TRUCKTYPE_VALIDATION_NOTES.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>

        <h3 className="h5 mt-4">Endpoint reference</h3>
        <Endpoint ep={ENDPOINTS.vehicleTypes} />
        <h4 className="h6 mt-3">Response fields</h4>
        <FieldReferenceTable fields={VEHICLE_TYPES_RESPONSE_FIELDS} />
        <CollapsibleTechnical title="vehicle-types cURL">
          <CodeBlock label="cURL">
            {partnerCurlExample(liveBase, "GET", ENDPOINTS.vehicleTypes.path, null)}
          </CodeBlock>
        </CollapsibleTechnical>

        <p className="text-muted small mt-4 mb-0">
          Next:{" "}
          <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => this.toggleTab("quoting")}>
            Create quote
          </button>{" "}
          (POST /v1/partner/quotes) once you have <code>trucktype</code> and <code>category</code>.
        </p>
      </div>
    );
  }

  renderQuoting() {
    return (
      <div className="qt-api-docs__section">
        <h2>Create quote</h2>
        <AudienceCallout>
          <code>POST /v1/partner/quotes</code> prices a trip and returns <code>sellingprice</code>, travel
          time, and a <code>quoteid</code> for booking. It does not return vehicle options - resolve{" "}
          <code>trucktype</code> and <code>category</code> from the catalog first.
        </AudienceCallout>

        <div className="qt-api-docs__callout qt-api-docs__callout--info mb-4">
          <p className="small mb-0">
            <strong>Prerequisite:</strong> call{" "}
            <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => this.toggleTab("vehicle-types")}>
              GET /v1/partner/vehicle-types
            </button>{" "}
            and pick <code>vehicles[].type</code> plus a <code>category</code> from that vehicle&apos;s list.
            Field types:{" "}
            <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => this.toggleTab("field-reference")}>
              Field reference
            </button>
            .
          </p>
        </div>

        <h3 className="h5 mt-4">Quote flow</h3>
        <PlainFlow steps={QUOTE_WORKFLOW} />

        <h3 className="h5 mt-4">Endpoint reference</h3>
        <Endpoint ep={ENDPOINTS.quote} />

        <h4 className="h6 mt-3">Request body - field reference</h4>
        <FieldReferenceTable fields={QUOTE_REQUEST_FIELDS} />

        <h4 className="h6 mt-3">Response body - field reference</h4>
        <FieldReferenceTable fields={QUOTE_RESPONSE_FIELDS} lastColumnLabel="Example" />

        <CollapsibleTechnical title="Full quotes cURL example">
          <CodeBlock label="cURL">{partnerCurlExample(liveBase, "POST", ENDPOINTS.quote.path, ENDPOINTS.quote.body)}</CodeBlock>
        </CollapsibleTechnical>
      </div>
    );
  }

  renderMeasurements() {
    return (
      <div className="qt-api-docs__section">
        <h2>Units &amp; formats</h2>
        <AudienceCallout>
          Prices are returned in <strong>South African cents</strong> (divide by 100 for rands). Distance is in
          metres; duration is in seconds.
        </AudienceCallout>
        <Table bordered responsive size="sm" className="qt-api-table bg-white">
          <thead className="table-light">
            <tr><th>What you see</th><th>Unit</th><th>Example</th></tr>
          </thead>
          <tbody>
            {MEASUREMENTS.map((m) => (
              <tr key={m.title}>
                <td><strong>{m.title}</strong></td>
                <td>{m.unit}</td>
                <td><code>{m.example}</code></td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  }

  renderBooking() {
    return (
      <div className="qt-api-docs__section">
        <h2>Book a trip</h2>
        <AudienceCallout>
          When you are happy with the price, your system confirms the quote. QikTruck creates the trip and
          starts finding a driver. You will need the customer&apos;s email and mobile number at this step.
        </AudienceCallout>
        <Endpoint ep={ENDPOINTS.acceptQuote} />
        <h4 className="h6 mt-3">Request body - field reference</h4>
        <FieldReferenceTable fields={ACCEPT_REQUEST_FIELDS} />
        <h4 className="h6 mt-3">Response body - field reference</h4>
        <FieldReferenceTable fields={ACCEPT_RESPONSE_FIELDS} lastColumnLabel="Example" />
        <CollapsibleTechnical title="Accept cURL example">
          <CodeBlock label="cURL">
            {partnerCurlExample(liveBase, "POST", ENDPOINTS.acceptQuote.path, ENDPOINTS.acceptQuote.body)}
          </CodeBlock>
        </CollapsibleTechnical>
      </div>
    );
  }

  renderOrders() {
    return (
      <div className="qt-api-docs__section">
        <h2>Track &amp; manage trips</h2>
        <AudienceCallout>
          After booking, check delivery progress from your system. You can list recent trips, look up one
          order, update customer contact details, or cancel before the driver completes the job.
        </AudienceCallout>
        <Endpoint ep={ENDPOINTS.listOrders} />
        <Endpoint ep={ENDPOINTS.trackOrder} />
        <Endpoint ep={ENDPOINTS.cancelOrder} />
        <Endpoint ep={ENDPOINTS.updateContacts} />

        <h4 className="h6 mt-3">Order object fields</h4>
        <FieldReferenceTable fields={ORDER_OBJECT_FIELDS} />
        <h4 className="h6 mt-3">Cancel request fields</h4>
        <FieldReferenceTable fields={CANCEL_REQUEST_FIELDS} />
        <h4 className="h6 mt-3">Update contacts request fields</h4>
        <FieldReferenceTable fields={CONTACTS_REQUEST_FIELDS} />

        <h3>Delivery status explained</h3>
        <div className="qt-api-flow qt-api-flow--compact">
          {ORDER_STATUS_FLOW.map((step, i) => (
            <React.Fragment key={step}>
              {i > 0 && <span className="qt-api-flow__arrow">→</span>}
              <span className="qt-api-flow__step">{ORDER_STATUSES.find((s) => s.code === step)?.plain || step}</span>
            </React.Fragment>
          ))}
        </div>
        <StatusCards statuses={ORDER_STATUSES} />

        <CollapsibleTechnical title="Polling and status codes (for developers)">
          <p className="text-muted small">
            Poll <code>GET /v1/partner/orders</code> or <code>POST .../track</code> until{" "}
            <code>status</code> is terminal. Webhooks are not available yet.
          </p>
          <Table bordered responsive size="sm" className="qt-api-table bg-white">
            <thead className="table-light">
              <tr><th>Code</th><th>Meaning</th></tr>
            </thead>
            <tbody>
              {ORDER_STATUSES.map((s) => (
                <tr key={s.code}><td><code>{s.code}</code></td><td>{s.detail}</td></tr>
              ))}
            </tbody>
          </Table>
        </CollapsibleTechnical>
      </div>
    );
  }

  renderFieldReference() {
    return (
      <div className="qt-api-docs__section">
        <h2>Field reference</h2>
        <AudienceCallout>
          Every Partner API payload field with <strong>JSON type</strong>, whether it is{" "}
          <strong>required</strong>, what it <strong>means</strong>, and an <strong>example</strong>.
          Enum-like values (<code>trucktype</code>, <code>category</code>) come from{" "}
          <code>GET /v1/partner/vehicle-types</code>.
        </AudienceCallout>
        <p className="text-muted small">
          Money fields use <strong>South African cents</strong> (integer). Coordinates are WGS-84 decimal
          degrees. Dates <code>YYYY-MM-DD</code>, times <code>HH:mm</code> in South Africa (SAST).
        </p>
        {FIELD_REFERENCE_SECTIONS.map((section) => (
          <div key={section.id} className="mb-4" id={"ref-" + section.id}>
            <h3 className="h5">{section.title}</h3>
            <FieldReferenceTable fields={section.fields} />
          </div>
        ))}
      </div>
    );
  }

  renderReference() {
    return (
      <div className="qt-api-docs__section">
        <h2>API reference</h2>
        <div className="qt-api-docs__callout qt-api-docs__callout--info">
          Request access in <a href={INTEGRATIONS_PATH}>Integrations → Partner API</a> if you have not received
          keys yet.
        </div>
        <Table bordered responsive size="sm" className="qt-api-table bg-white">
          <thead className="table-light">
            <tr><th>Area</th><th>Endpoints</th></tr>
          </thead>
          <tbody>
            {API_CATALOG.map((row) => (
              <tr key={row.area}>
                <td><strong>{row.area}</strong></td>
                <td><code>{row.routes}</code></td>
              </tr>
            ))}
          </tbody>
        </Table>
        <h3 className="mt-4">Detailed endpoints</h3>
        <Endpoint ep={ENDPOINTS.vehicleTypes} />
        <Endpoint ep={ENDPOINTS.quote} />
        <Endpoint ep={ENDPOINTS.acceptQuote} />
        <Endpoint ep={ENDPOINTS.trackOrder} />
        <Endpoint ep={ENDPOINTS.listOrders} />
        <Endpoint ep={ENDPOINTS.cancelOrder} />
        <Endpoint ep={ENDPOINTS.updateContacts} />
      </div>
    );
  }

  renderErrors() {
    return (
      <div className="qt-api-docs__section">
        <h2>When something goes wrong</h2>
        <AudienceCallout>
          If a request fails, your developer will see a short error message. Common causes: wrong API key,
          missing customer details, or trying to cancel a trip that already finished.
        </AudienceCallout>
        <CollapsibleTechnical title="HTTP error codes (for developers)">
          <ErrorTable codes={ERROR_CODES} />
        </CollapsibleTechnical>
        <p className="text-muted small mt-3">
          Need help? Email{" "}
          <a href="mailto:support@qiktruck.co.za?subject=Partner%20API">support@qiktruck.co.za</a>.
        </p>
      </div>
    );
  }

  renderPlans() {
    return (
      <div className="qt-api-docs__section" id="plans">
        <h2>Plans &amp; pricing</h2>
        <AudienceCallout>
          API plans control monthly quotas, vehicle catalog, and features.{" "}
          <strong>Trip freight is billed separately</strong> (prepaid or invoice) - not included in the plan price.
          Sandbox testing is free; Starter and Operations use PayFast <strong>recurring monthly subscriptions</strong>.
        </AudienceCallout>
        <div className="row g-3 mb-4">
          {PARTNER_PLANS.map((plan) => (
            <div className="col-md-6 col-xl-3" key={plan.id}>
              <div className="qt-api-docs__callout qt-api-docs__callout--info h-100 mb-0">
                <h3 className="h5">{plan.headline}</h3>
                <p className="h6 text-primary">{plan.price_label}</p>
                <p className="small text-muted">{plan.billing}</p>
                <ul className="small mb-0 ps-3">
                  {plan.highlights.map((h) => (
                    <li key={h}>{h}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
        <h3 className="h5">Booking packs (overage)</h3>
        <p className="text-muted small">
          When you are near your monthly booking limit, buy a one-off pack via PayFast. Bonus bookings apply to the current calendar month only.
        </p>
        <Table size="sm" bordered responsive className="small">
          <thead><tr><th>Pack</th><th>Price</th><th>Note</th></tr></thead>
          <tbody>
            {ACCEPT_PACKS_DOCS.map((p) => (
              <tr key={p.id}><td>{p.label}</td><td>{p.price}</td><td>{p.note}</td></tr>
            ))}
          </tbody>
        </Table>
        <p className="mt-3 mb-0">
          Subscribe or buy packs in{" "}
          <a href={PARTNER_PLANS_PATH}>Partner API plans</a>.
        </p>
      </div>
    );
  }

  renderBilling() {
    return (
      <div className="qt-api-docs__section">
        <h2>Billing &amp; payment</h2>
        <AudienceCallout>
          By default, Partner API bookings are charged to <strong>your QikTruck business account</strong>.
          Alternatively, pass <code>payment_payer: &quot;end_customer&quot;</code> when you book to receive a{" "}
          <strong>QikTruck PayFast payment page</strong> URL for your shopper.
        </AudienceCallout>
        <h3 className="h5">Who pays QikTruck</h3>
        {PAYMENT_PAYERS.map((mode) => (
          <div key={mode.id} className="qt-api-docs__callout qt-api-docs__callout--info mb-3">
            <h3 className="h6">{mode.title}</h3>
            <p className="mb-0 small">{mode.summary}</p>
          </div>
        ))}
        <h3 className="h5 mt-4">How your business settles</h3>
        {BILLING_MODES.map((mode) => (
          <div key={mode.id} className="qt-api-docs__callout qt-api-docs__callout--info mb-3">
            <h3 className="h5">{mode.title}</h3>
            <p className="mb-2">{mode.summary}</p>
            <ul className="small mb-0 qt-api-docs__billing-list">
              <li><strong>Who pays QikTruck:</strong> {mode.who_pays}</li>
              <li><strong>When:</strong> {mode.when}</li>
              <li><strong>Your customer:</strong> {mode.end_customer}</li>
            </ul>
          </div>
        ))}
        <p className="text-muted">
          Set your default <code>payment_payer</code> under Integrations → Configuration → Billing, or override per
          booking when you call <code>{`POST /quotes/{id}/accept`}</code>. View merchant invoices in{" "}
          <a href="/billing">Billing</a>.
        </p>
        <h3>Damage &amp; dispute claims</h3>
        <p className="text-muted">{CLAIMS_INFO.note}</p>
        <a
          href={CLAIMS_INFO.form_url}
          className="btn btn-outline-primary btn-sm"
          target="_blank"
          rel="noopener noreferrer"
        >
          Open claims form
        </a>
      </div>
    );
  }

  renderContent() {
    switch (this.state.activeTab) {
      case "compare": return <CompareTable />;
      case "request-access": return this.renderRequestAccess();
      case "plans": return this.renderPlans();
      case "auth": return this.renderAuth();
      case "measurements": return this.renderMeasurements();
      case "field-reference": return this.renderFieldReference();
      case "vehicle-types": return this.renderVehicleTypes();
      case "quoting": return this.renderQuoting();
      case "booking": return this.renderBooking();
      case "orders": return this.renderOrders();
      case "billing": return this.renderBilling();
      case "reference": return this.renderReference();
      case "errors": return this.renderErrors();
      default: return this.renderOverview();
    }
  }

  render() {
    const { productSwitcher } = this.props;
    return (
      <div className="qt-api-docs">
        <div className="qt-api-docs__hero qt-api-docs__hero--partner">
          <Container>
            {productSwitcher}
            <span className="qt-api-docs__badge qt-api-docs__badge--partner">Partner API</span>
            <h1>Connect your software to QikTruck deliveries</h1>
            <p className="lead mb-0">
              For ERP, TMS, and custom backends. Clear guides for business teams and full technical reference
              for developers.
            </p>
          </Container>
        </div>
        <Container fluid>
          <Row>
            <Col lg={3} className="qt-api-docs__sidebar d-none d-lg-block">
              <div className="sticky-top pt-3" style={{ top: "1rem" }}>
                <Nav className="flex-column">
                  {NAV_GROUPS.map((group) => (
                    <React.Fragment key={group.label}>
                      <div className="qt-api-docs__nav-group">{group.label}</div>
                      {group.items.map((item) => (
                        <NavItem key={item.id}>
                          <NavLink
                            className={this.state.activeTab === item.id ? "active" : ""}
                            onClick={() => this.toggleTab(item.id)}
                          >
                            {item.label}
                          </NavLink>
                        </NavItem>
                      ))}
                    </React.Fragment>
                  ))}
                </Nav>
                <SidebarActions
                  secondaryHref="/api-docs?product=embed"
                  secondaryLabel="Website embed docs"
                  primaryHref={INTEGRATIONS_PATH}
                  primaryLabel="Integrations hub"
                />
              </div>
            </Col>
            <Col lg={9} className="qt-api-docs__main">
              <div className="d-lg-none mb-3">
                <select
                  className="form-select"
                  value={this.state.activeTab}
                  onChange={(e) => this.toggleTab(e.target.value)}
                >
                  {NAV_GROUPS.flatMap((g) => g.items).map((item) => (
                    <option key={item.id} value={item.id}>{item.label}</option>
                  ))}
                </select>
              </div>
              {this.renderContent()}
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

export default PartnerApiDocs;
